/**
 * O TEMPO MÍNIMO de cada etapa na tela (17/09/2026) — o mixin que separa "o
 * que o turno está fazendo AGORA" (`passoAtualDoTurno`, recalculado a cada
 * evento do fio) do "o que está ESCRITO na linha de raciocínio"
 * (`passoExibidoDoTurno`, que o `chat-message.vue` entrega ao
 * `AssistantBlocks`).
 *
 * ## Por que existe
 *
 * Medido no fio real em 17/09/2026 (escritório 83, backend local, pergunta
 * "me dá um panorama do meu escritório"), os `tool_call` de um mesmo lote
 * chegam praticamente juntos:
 *
 *     3.91s  tool_call get_office_360           → "Consultando a visão 360 do escritório..."
 *     4.12s  tool_call list_alertas_atrasados   → "Consultando alertas..."
 *     4.24s  tool_call get_funil_vendas         → "Consultando funil de oportunidades..."
 *
 * Três frases em 330 ms. Sem trava, a linha pisca e o consultor não lê
 * nenhuma delas — fica pior do que o "Pensando..." genérico que a mudança
 * veio substituir.
 *
 * ## A régua
 *
 * `TEMPO_MINIMO_DO_PASSO_MS = 900`. É a faixa que o próprio repo já declara
 * para transição percebida como suave ("minimum loading duration of
 * 600-1000ms", `aawz-web-frontend/CLAUDE.md`, UX Principle) e é o bastante
 * para ler uma frase de 4 a 6 palavras. Mais curto não resolve os 330 ms
 * medidos acima; mais longo faria a tela ficar visivelmente atrás do turno.
 *
 * ⚠️ A fila guarda só o ÚLTIMO valor desejado, nunca todos: quando a espera
 * termina, o que entra é o que é verdade NAQUELE instante, não o passo que
 * pediu a espera. Enfileirar cada rótulo faria a tela acumular atraso sem
 * teto numa rajada e, no fim, anunciar consultas que já terminaram. Assim o
 * atraso máximo é UM `TEMPO_MINIMO_DO_PASSO_MS`, sempre.
 *
 * ## O que NÃO espera
 *
 * Duas coisas, e as duas pelo mesmo motivo — não são etapa:
 *
 * - o GENÉRICO de preparo ("Pensando...") NUNCA segura a tela. Ele não tem o
 *   que ler, e segurá-lo por 900 ms seria manter na tela exatamente o que o
 *   owner pediu para tirar ("não ficar com 'pensando' durante um tempo"): a
 *   primeira etapa conhecida entra no mesmo tick, mesmo que chegue 50 ms
 *   depois do `start`;
 * - `aguardandoDecisao` ("Aguardando sua decisão", US-13) é ESTADO — o run
 *   parou e espera o clique do consultor no cartão. Segurá-lo por até 900 ms
 *   deixaria a linha dizendo "Consultando..." com o cartão de confirmação já
 *   na tela, que é a contradição que o indicador único existe para não ter.
 *   Ele entra na hora E cancela qualquer troca marcada.
 *
 * ## ★ [T064] O FECHO: "Pensando por X", depois que o turno termina
 *
 * O mesmo arquivo passa a responder pelas duas pontas do relógio do turno: o
 * passo corrente enquanto ele roda (acima) e, quando ele fecha, **quanto
 * tempo levou** — registrado na mensagem, como o ChatGPT e o Claude fazem.
 * A fonte é `duracao_s`, que `apiChat._onEventEnd` já carimba na mensagem: não
 * há estado novo, nem no store nem aqui.
 *
 * ## Contrato com quem usa
 *
 * O componente precisa expor:
 *  - `passoAtualDoTurno` (String) — a etapa corrente, de `utils/rotulo-do-turno.js`;
 *  - `persona` (Object) — de onde sai o `textoDePreparo` que identifica o genérico;
 *  - `aguardandoDecisao` (Boolean) — a prop que vem do `messages.vue`;
 *  - [T064] `duracaoDoTurno` (Number|null) — o `message.duracao_s`;
 *  - [T064] `isStreaming` (Boolean) — enquanto roda, quem conta é o cronômetro
 *    ao vivo da `raciocinio-linha`; o rótulo é o FECHO, não o durante.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila).
 */
import { TEMPO_DO_TURNO } from '@modules/aawz-intelligence-v2/constants/TextChat'
import { ehRotuloDePreparo } from '@modules/aawz-intelligence-v2/utils/rotulo-do-turno'

// Ver "A régua", acima — o valor não é escolhido no olho: é a faixa de
// suavidade já declarada no CLAUDE.md do repo, ancorada nos 330 ms medidos.
export const TEMPO_MINIMO_DO_PASSO_MS = 900

const SEGUNDOS_POR_MINUTO = 60

/**
 * ★ [T064] Abaixo de quantos segundos o turno NÃO ganha rótulo de tempo.
 *
 * **3 s, e o número sai daqui de cima:** cada etapa é segurada por
 * `TEMPO_MINIMO_DO_PASSO_MS` (900 ms) antes de poder ser trocada, então um
 * turno de menos de 3 s exibiu no máximo duas — o genérico de preparo e uma
 * etapa. Ele não chegou a ser uma espera; foi percebido como imediato.
 *
 * Carimbar *"Pensando por 2 segundos"* embaixo de uma resposta instantânea é
 * ruído, e ruído é pior que silêncio: é a MESMA régua que o contador ao vivo
 * já aplica na `raciocinio-linha`, que não aparece em 0 s
 * (`segundosExibidos > 0`). Aqui o piso é maior porque o rótulo é permanente
 * — fica na conversa reaberta —, enquanto o contador some com o turno.
 */
export const LIMIAR_DO_ROTULO_DE_TEMPO_S = 3

/**
 * ★ [T064] "Pensando por 42 s" · "Pensando por 1 min 12 s" · "Pensando por
 * 2 min". String VAZIA para o turno curto, para o ausente e para o inválido —
 * quem chama decide mostrando ou não, sem precisar saber do limiar.
 *
 * Arredonda ANTES de partir em minutos: sem isso, 59,7 s viraria
 * "0 min 59,7 s". A casa decimal que a `raciocinio-linha` mostra no resumo
 * ("4,2s") não vem para cá de propósito — este é o título da linha, e o
 * ChatGPT/Claude também arredondam para inteiro no cabeçalho.
 *
 * @param {number|null} segundos `message.duracao_s`
 * @returns {string}
 */
export const rotuloDeTempoDoTurno = (segundos) => {
  if (typeof segundos !== 'number' || !isFinite(segundos)) return ''
  const inteiros = Math.round(segundos)
  if (inteiros < LIMIAR_DO_ROTULO_DE_TEMPO_S) return ''
  const corpo =
    inteiros < SEGUNDOS_POR_MINUTO
      ? TEMPO_DO_TURNO.segundos(inteiros)
      : TEMPO_DO_TURNO.minutos(
          Math.floor(inteiros / SEGUNDOS_POR_MINUTO),
          inteiros % SEGUNDOS_POR_MINUTO
        )
  return `${TEMPO_DO_TURNO.prefixo} ${corpo}`
}

export const passoDoTurnoComTempoMinimo = {
  data() {
    return {
      // O que está ESCRITO na linha. Nasce vazio e recebe o primeiro passo
      // sem espera nenhuma — a trava é para a TROCA, não para a estreia.
      passoExibidoDoTurno: '',
    }
  },
  computed: {
    // O que está na tela é uma ETAPA de verdade (e por isso merece ser lida)?
    // O genérico de preparo e a linha ainda vazia não prendem nada.
    passoPrendeATela() {
      const inicial = (this.persona && this.persona.textoDePreparo) || ''
      return !ehRotuloDePreparo(this.passoExibidoDoTurno, inicial)
    },
    /**
     * ★ [T064] O FECHO do turno. Vazio enquanto ele roda — aí quem conta é o
     * cronômetro ao vivo da `raciocinio-linha`, e dois relógios na mesma linha
     * leriam como repetição.
     *
     * Derivado, nunca guardado: a fonte é `duracao_s`, que já vem carimbado na
     * mensagem. No histórico antigo ele é `null` e o rótulo some sozinho — não
     * há como saber quanto durou um turno que ninguém mediu, e inventar seria
     * pior que calar.
     */
    rotuloDoTempoDoTurno() {
      if (this.isStreaming) return ''
      return rotuloDeTempoDoTurno(this.duracaoDoTurno)
    },
  },
  watch: {
    passoAtualDoTurno: {
      immediate: true,
      handler(novo) {
        this.pedirPassoDoTurno(novo)
      },
    },
  },
  beforeDestroy() {
    this.limparEsperaDoPasso()
  },
  methods: {
    pedirPassoDoTurno(novo) {
      const texto = novo || ''
      if (texto === this.passoExibidoDoTurno) return

      // Estreia do turno, genérico de preparo cedendo o lugar à primeira
      // etapa, ou a decisão do consultor: sem espera nenhuma.
      if (!this.passoPrendeATela || this.aguardandoDecisao) {
        this.limparEsperaDoPasso()
        this.escreverPassoDoTurno(texto)
        return
      }

      const decorrido = Date.now() - this._passoExibidoDesde
      if (decorrido >= TEMPO_MINIMO_DO_PASSO_MS) {
        this.escreverPassoDoTurno(texto)
        return
      }

      // Já há uma troca marcada, e ela lê o valor CORRENTE quando disparar:
      // não há o que reagendar. O instante do disparo seria o MESMO de
      // qualquer jeito — o prazo é contado de `_passoExibidoDesde`, que não
      // se move enquanto a tela não troca —, então reagendar seria só um
      // `clearTimeout`/`setTimeout` por evento do fio, e o fio manda muitos.
      if (this._esperaDoPasso) return
      const self = this
      this._esperaDoPasso = setTimeout(function () {
        self._esperaDoPasso = null
        self.escreverPassoDoTurno(self.passoAtualDoTurno || '')
      }, TEMPO_MINIMO_DO_PASSO_MS - decorrido)
    },
    // Fora do `data()` de propósito: `_passoExibidoDesde` e `_esperaDoPasso`
    // são relógio e handle de timer, não estado de tela — mesmo padrão do
    // `_revelacao` em `chat-message.revelacao.js`.
    escreverPassoDoTurno(texto) {
      this.passoExibidoDoTurno = texto
      this._passoExibidoDesde = Date.now()
    },
    limparEsperaDoPasso() {
      if (!this._esperaDoPasso) return
      clearTimeout(this._esperaDoPasso)
      this._esperaDoPasso = null
    },
  },
}

export default passoDoTurnoComTempoMinimo
